import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Lock, Unlock, AlertTriangle, Shield, CheckCircle, XCircle, ArrowLeft, ArrowRight, RefreshCw, BookOpen } from 'lucide-react';

const WebBrowserSecuritySimulator = ({ scenarios = [], onComplete, challenge }) => {
    const [currentScenario, setCurrentScenario] = useState(0);
    const [userDecisions, setUserDecisions] = useState({});
    const [showFeedback, setShowFeedback] = useState(false);
    const [currentUrl, setCurrentUrl] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        console.log('WebBrowserSecuritySimulator received scenarios:', scenarios);
        if (scenarios.length > 0) {
            const scenario = scenarios[currentScenario] || scenarios[0];
            setCurrentUrl(scenario.url || 'https://example.com');
        } else {
            // Create fallback scenario if none provided
            setCurrentUrl('https://secure-banking-login.net/account');
        }
    }, [scenarios, currentScenario]);

    const getCurrentScenario = () => {
        if (scenarios.length === 0) {
            return {
                scenarioType: "browser",
                url: "https://secure-banking-login.net/account",
                pageType: "phishing_login",
                arenaId: "phishing_social_engineering",
                isThreat: true,
                correctAction: "close_tab",
                indicators: ["Suspicious domain", "Missing security features"],
                feedback: "This is a phishing attempt.",
                points: 50
            };
        }
        return scenarios[currentScenario] || scenarios[0];
    };

    const handleUserAction = (action) => {
        const scenario = getCurrentScenario();
        const isCorrect = action === scenario.correctAction;

        const decision = {
            scenarioId: scenario.id || `scenario-${currentScenario}`,
            action: action,
            correct: isCorrect,
            timestamp: Date.now()
        };

        setUserDecisions(prev => ({
            ...prev,
            [currentScenario]: decision
        }));

        setShowFeedback(true);

    };

    const handleContinue = () => {
        setShowFeedback(false);
        if (currentScenario < scenarios.length - 1) {
            setCurrentScenario(prev => prev + 1);
        } else {
            completeSimulation();
        }
    };

    const handleLearnMore = () => {
        const scenario = getCurrentScenario();
        if (challenge?.metadata) {
            window.open(challenge.metadata, '_blank', 'noopener,noreferrer');
        } else {
            // Fallback to original behavior
            const arenaId = scenario.arenaId || 'secure_browsing';
            navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
        }
    };
    const completeSimulation = () => {
        const decisions = Object.values(userDecisions);
        const correctCount = decisions.filter(d => d.correct).length;
        const total = Math.max(decisions.length, 1);

        const summary = {
            correct: correctCount,
            total: total,
            score: Math.round((correctCount / total) * 100),
            decisions: userDecisions
        };

        if (onComplete) {
            onComplete(summary);
        }
    };

    const renderPageContent = () => {
        const scenario = getCurrentScenario();

        switch (scenario.pageType) {
            case 'phishing_login':
                return (
                    <div className="min-h-[100dvh] bg-blue-50 flex items-center justify-center">
                        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
                            <h2 className="text-2xl font-bold text-blue-900 mb-6 text-center">Secure Banking Login</h2>
                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                                    <input type="text" className="w-full p-3 border border-gray-300 rounded" placeholder="Enter account number" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                    <input type="password" className="w-full p-3 border border-gray-300 rounded" placeholder="Enter password" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Social Security Number</label>
                                    <input type="text" className="w-full p-3 border border-gray-300 rounded" placeholder="XXX-XX-XXXX" />
                                </div>
                                <button type="button" className="w-full bg-blue-600 text-white p-3 rounded hover:bg-blue-700">
                                    Login Securely
                                </button>
                            </form>
                        </div>
                    </div>
                );

            case 'malicious_download':
                return (
                    <div className="min-h-[100dvh] bg-gray-100 p-4">
                        <div className="max-w-4xl mx-auto">
                            <h1 className="text-3xl font-bold text-center mb-8">Free Software Downloads</h1>
                            <div className="bg-white p-6 rounded-lg shadow border-2 border-green-500">
                                <h3 className="text-xl font-bold text-green-600 mb-2">Premium Antivirus FREE!</h3>
                                <p className="text-gray-600 mb-4">Complete protection - Normally $99.99, now FREE!</p>
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-sm text-gray-500">Size: {scenario.downloadFile?.size || '2.3 MB'}</span>
                                    <span className="text-green-600 font-bold">100% FREE</span>
                                </div>
                                <button className="w-full bg-green-500 text-white p-3 rounded font-bold hover:bg-green-600 animate-pulse">
                                    DOWNLOAD NOW - FREE
                                </button>
                            </div>
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="min-h-[100dvh] bg-white p-4 sm:p-8">
                        <div className="max-w-4xl mx-auto">
                            <h1 className="text-3xl font-bold mb-6">Security Scenario</h1>
                            <div className="bg-white p-6 rounded-lg shadow">
                                <p className="text-gray-700">
                                    Examine this page carefully and determine if there are any security concerns.
                                </p>
                            </div>
                        </div>
                    </div>
                );
        }
    };

    const renderBrowserChrome = () => {
        const scenario = getCurrentScenario();
        const hasSSL = currentUrl.startsWith('https://');

        return (
            <div className="bg-gray-200 p-2 space-y-2">
                <div className="flex items-center space-x-2 bg-white rounded p-2">
                    <button className="p-1 hover:bg-gray-100 rounded">
                        <ArrowLeft size={16} className="text-gray-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                        <ArrowRight size={16} className="text-gray-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                        <RefreshCw size={16} className="text-gray-600" />
                    </button>

                    <div className="flex-1 flex items-center bg-white border rounded px-3 py-2">
                        <div className="flex items-center mr-2">
                            {hasSSL ? (
                                <Lock size={14} className="text-green-600" />
                            ) : (
                                <Unlock size={14} className="text-red-600" />
                            )}
                        </div>
                        <span className="text-sm font-mono flex-1 truncate">{currentUrl}</span>
                    </div>
                </div>
            </div>
        );
    };

    const renderActionButtons = () => {
        if (showFeedback) return null;

        return (
            <div className="absolute bottom-4 right-4 space-y-2">
                <div className="bg-white rounded-lg shadow-lg p-4 border">
                    <h3 className="font-semibold mb-3 text-gray-800">What should you do?</h3>
                    <div className="space-y-2">
                        <button
                            onClick={() => handleUserAction('proceed')}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center"
                        >
                            <CheckCircle size={16} className="mr-2" />
                            This site is safe
                        </button>

                        <button
                            onClick={() => handleUserAction('close_tab')}
                            className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center"
                        >
                            <XCircle size={16} className="mr-2" />
                            Close this tab
                        </button>

                        <button
                            onClick={() => handleUserAction('verify_independently')}
                            className="w-full px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                        >
                            Verify independently
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderFeedback = () => {
        if (!showFeedback) return null;

        const scenario = getCurrentScenario();
        const decision = userDecisions[currentScenario];
        const isCorrect = decision?.correct;

        return (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-3xl mx-4">
                    <div className="flex items-center mb-4">
                        {isCorrect ? (
                            <CheckCircle size={24} className="text-green-600 mr-3" />
                        ) : (
                            <XCircle size={24} className="text-red-600 mr-3" />
                        )}
                        <h3 className="text-xl font-bold">
                            {isCorrect ? 'Correct!' : 'Incorrect'}
                        </h3>
                    </div>

                    <p className="text-gray-700 mb-4">{scenario.feedback}</p>

                    {scenario.indicators && scenario.indicators.length > 0 && (
                        <div className="mb-4">
                            <h4 className="font-semibold mb-2">Security Indicators:</h4>
                            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                {scenario.indicators.map((indicator, index) => (
                                    <li key={index}>{indicator}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                            +{scenario.points || 50} XP
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end items-center gap-4">
                        <button
                            onClick={handleLearnMore}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
                        >
                            <BookOpen size={16} />
                            Learn More
                        </button>
                        <button
                            onClick={handleContinue}
                            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="h-full flex flex-col bg-gray-100 relative">
            <div className="bg-gray-300 p-2 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="text-sm font-medium text-gray-700">Browser Security Simulation</div>
                <div className="text-sm text-gray-600">
                    Scenario {currentScenario + 1} of {Math.max(scenarios.length, 1)}
                </div>
            </div>

            {renderBrowserChrome()}

            <div className="flex-1 relative overflow-auto">
                {renderPageContent()}
                {renderActionButtons()}
                {renderFeedback()}
            </div>
        </div>
    );
};

export default WebBrowserSecuritySimulator;